import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { PRODUCT_LINES } from '@data/index';

/**
 * SecondaryNav — horizontal nav bar + Products mega menu flyout
 *
 * Matches live MRT exactly:
 *   Home | Products (underline) | Tool Finder | Contact us
 *
 * Account-level pages (My orders, Cases, Invoices, Reports, Track data)
 * live ONLY in the left drawer Navigation.jsx under "My account".
 *
 * Products click/hover opens mega menu panel:
 *   Left section: Top Hammer (By application col + By product col with cascading sub-menus)
 *   Right section: DTH (Hammers/Bits/Drill pipes with cascading sub-menus)
 *   Far right: Guided Tool Finder promo card with image
 *
 * Cascading: hover a category → sub-menu appears to its right (desktop)
 * Mobile: full-width stacked accordion
 */

const NAV_ITEMS = [
  { label: 'Home', to: '/' },
  { label: 'Products', to: '/products', hasMegaMenu: true },
  { label: 'Value calculators', hasValueCalcMenu: true },
  { label: 'Tool Finder', to: '/tool-finder' },
  { label: 'Contact us', to: '/contact' },
];

// Value calculators — small dropdown, not a full mega menu. Grows as
// each RT_XXX calculator ships. Each live entry is deployed at its own
// Azure webapp URL and reached via same-tab navigation (the calculator
// adopts MRT's chrome so the UX reads as a sub-page, not a separate app).
//
// Down the Hole (RT_006) went live 2026-08-17, joining Resharpening
// (RT_004). Its trunk-item-9 block — "blocked on deployment" — is cleared:
// DTH is deployed and confirmed calling admin-api with its own Layer 1
// credentials. URL confirmed by Vandana the same day.
const VALUE_CALCULATORS = [
  {
    id: 'resharpening',
    label: 'Resharpening value calculator',
    description: 'Grinding machine ROI + cost breakdown',
    href: 'https://resharpeningcalculator.azurewebsites.net/',
    available: true,
  },
  {
    id: 'dth',
    label: 'Down the hole value calculator',
    description: 'Bit + hammer cost per metre and savings',
    href: 'https://dthcalculator.azurewebsites.net/',
    available: true,
  },
  {
    id: 'th-bench',
    label: 'Top hammer bench drilling calculator',
    description: 'Coming soon',
    href: null,
    available: false,
  },
];

const topHammer = PRODUCT_LINES['top-hammer'];
const dth = PRODUCT_LINES['down-the-hole'];
const rotary = PRODUCT_LINES['rotary'];
const raiseboring = PRODUCT_LINES['raiseboring'];
const services = PRODUCT_LINES['services'];

// Per Rock Tools taxonomy: Rotary & Raiseboring is ONE product line — the
// mega menu shows their categories under a single column. Data model keeps
// them as separate PL nodes (so `commerceEnabled: false` gates each
// independently and product routing stays clean); the merge is nav-only.
//
// Raiseboring collapses into a single cascade entry ("Raiseboring") whose
// second-level items are Cutters / Reamer heads / Stems. Reamer heads is
// itself a parent category with its own landing page (3 head-type tiles),
// so we point it at that landing route rather than trying to nest a third
// cascade level (CascadeItem only supports one level of subs).
const rotaryAndRbAvailable = !!(rotary?.available || raiseboring?.available);
const rotaryAndRbTitle =
  rotary?.available && raiseboring?.available
    ? 'Rotary & Raiseboring drilling tools'
    : (rotary?.available ? rotary.title : raiseboring?.title);

const raiseboringMenuNode = raiseboring?.available
  ? {
      id: 'raiseboring',
      title: 'Raiseboring',
      route: raiseboring.route,
      subcategories: (raiseboring.categories || []).map((c) => ({
        id: c.id,
        title: c.title,
        route: c.route,
      })),
    }
  : null;

const rotaryAndRbCategories = [
  ...(rotary?.available ? (rotary.categories || []) : []),
  ...(raiseboringMenuNode ? [raiseboringMenuNode] : []),
];

// ============================================================================
// CASCADING SUB-MENU — appears on hover/click to the right of parent item
// ============================================================================

function CascadeItem({ item, onNavigate }) {
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef(null);
  const hasSubs = item.subcategories && item.subcategories.length > 0;

  const handleMouseEnter = () => {
    clearTimeout(timeoutRef.current);
    if (hasSubs) setIsOpen(true);
  };
  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setIsOpen(false), 150);
  };

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  return (
    <div
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {hasSubs ? (
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between gap-2 px-0 py-2 text-sm font-sandvik-text text-carbon-black hover:text-electric-blue transition-colors text-left"
        >
          <span className="truncate">{item.title}</span>
          <span className="material-symbols-outlined text-base text-n4 flex-shrink-0">chevron_right</span>
        </button>
      ) : (
        <Link
          to={item.route || '#'}
          onClick={onNavigate}
          className="block py-2 text-sm font-sandvik-text text-carbon-black hover:text-electric-blue transition-colors"
        >
          {item.title}
        </Link>
      )}

      {/* Cascading sub-menu */}
      {hasSubs && isOpen && (
        <div
          className="absolute left-full top-0 ml-0 bg-white border border-n2 shadow-md min-w-[220px] z-10 py-1"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {item.subcategories.map((sub) => (
            <Link
              key={sub.id}
              to={sub.route || '#'}
              onClick={onNavigate}
              className="block px-4 py-2 text-sm font-sandvik-text text-carbon-black hover:text-electric-blue hover:bg-n1 transition-colors"
            >
              {sub.title}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MEGA MENU PANEL
// ============================================================================

function MegaMenuPanel({ onClose }) {
  const panelRef = useRef(null);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div
      ref={panelRef}
      className="absolute left-0 right-0 top-full bg-white border-b border-n2 shadow-lg z-50"
      role="menu"
    >
      <div className="sandvik-container py-6">
        <div className="flex gap-0">

          {/* TOP HAMMER SECTION */}
          <div className="flex-1 pr-6 border-r border-n2">
            <h3 className="text-base font-normal font-sandvik-text text-carbon-black mb-4">
              {topHammer.title}
            </h3>
            <div className="grid grid-cols-2 gap-x-8">
              <div>
                <p className="text-sm font-normal font-sandvik-text text-carbon-black mb-2">
                  By application
                </p>
                {topHammer.byApplication?.map((app) => (
                  <CascadeItem key={app.id} item={app} onNavigate={onClose} />
                ))}
              </div>
              <div>
                <p className="text-sm font-normal font-sandvik-text text-carbon-black mb-2">
                  By product
                </p>
                {topHammer.categories?.map((cat) => (
                  <CascadeItem key={cat.id} item={cat} onNavigate={onClose} />
                ))}
              </div>
            </div>
          </div>

          {/* DTH SECTION */}
          <div className="w-[200px] px-6 border-r border-n2">
            <h3 className="text-base font-normal font-sandvik-text text-carbon-black mb-4">
              {dth.title}
            </h3>
            {dth.categories?.map((cat) => (
              <CascadeItem key={cat.id} item={cat} onNavigate={onClose} />
            ))}
          </div>

          {/* ROTARY & RAISEBORING SECTION — hidden while both unavailable */}
          {rotaryAndRbAvailable && (
            <div className="w-[200px] px-6 border-r border-n2">
              <h3 className="text-base font-normal font-sandvik-text text-carbon-black mb-4">
                {rotaryAndRbTitle}
              </h3>
              {rotaryAndRbCategories.map((cat) => (
                <CascadeItem key={cat.id} item={cat} onNavigate={onClose} />
              ))}
            </div>
          )}

          {/* SERVICES SECTION — Group S1 (Resharpening); hidden while unavailable */}
          {services?.available && (
            <div className="w-[200px] px-6 border-r border-n2">
              <h3 className="text-base font-normal font-sandvik-text text-carbon-black mb-4">
                {services.title}
              </h3>
              {services.categories?.map((cat) => (
                <CascadeItem key={cat.id} item={cat} onNavigate={onClose} />
              ))}
            </div>
          )}

          {/* PROMO CARD: Guided Tool Finder */}
          <div className="w-[240px] pl-6 flex-shrink-0">
            <div className="flex justify-end mb-2">
              <button
                onClick={onClose}
                className="p-1 hover:bg-n1 transition-colors"
                aria-label="Close menu"
              >
                <span className="material-symbols-outlined text-lg text-n5">close</span>
              </button>
            </div>

            <Link
              to="/tool-finder"
              onClick={onClose}
              className="block group"
            >
              <div className="relative aspect-[4/3] bg-n1 overflow-hidden mb-3">
                <img
                  src="/images/home/guided-tool-finder.jpg"
                  alt="Guided Tool Finder"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="material-symbols-outlined text-4xl text-n4">build</span>
                </div>
              </div>
              <p className="text-sm font-normal font-sandvik-text text-carbon-black group-hover:text-electric-blue transition-colors">
                Guided Tool Finder
              </p>
              <p className="text-xs text-n5 font-sandvik-text mt-0.5">
                The right tool for the right job
              </p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MOBILE MEGA MENU — stacked accordion
// ============================================================================

function MobileMegaMenu({ onClose }) {
  const [expandedSection, setExpandedSection] = useState(null);
  const [expandedCategory, setExpandedCategory] = useState(null);

  const toggleSection = (id) => {
    setExpandedSection(expandedSection === id ? null : id);
    setExpandedCategory(null);
  };

  const toggleCategory = (id) => {
    setExpandedCategory(expandedCategory === id ? null : id);
  };

  const renderCategoryList = (categories) =>
    categories?.map((cat) => {
      const hasSubs = cat.subcategories && cat.subcategories.length > 0;
      const isExpanded = expandedCategory === cat.id;

      return (
        <div key={cat.id}>
          {hasSubs ? (
            <>
              <button
                onClick={() => toggleCategory(cat.id)}
                className="w-full flex items-center justify-between py-2.5 px-4 text-sm font-sandvik-text text-carbon-black"
              >
                <span>{cat.title}</span>
                <span className="material-symbols-outlined text-base text-n4">
                  {isExpanded ? 'expand_less' : 'expand_more'}
                </span>
              </button>
              {isExpanded && (
                <div className="pl-8 pb-2">
                  {cat.subcategories.map((sub) => (
                    <Link
                      key={sub.id}
                      to={sub.route || '#'}
                      onClick={onClose}
                      className="block py-2 text-sm text-n6 font-sandvik-text hover:text-electric-blue"
                    >
                      {sub.title}
                    </Link>
                  ))}
                </div>
              )}
            </>
          ) : (
            <Link
              to={cat.route || '#'}
              onClick={onClose}
              className="block py-2.5 px-4 text-sm font-sandvik-text text-carbon-black hover:text-electric-blue"
            >
              {cat.title}
            </Link>
          )}
        </div>
      );
    });

  return (
    <div className="absolute left-0 right-0 top-full bg-white border-b border-n2 shadow-lg z-50 max-h-[70vh] overflow-y-auto">
      {/* Top Hammer */}
      <div className="border-b border-n2">
        <button
          onClick={() => toggleSection('th')}
          className="w-full flex items-center justify-between px-4 py-3 text-sm font-normal font-sandvik-text text-carbon-black"
        >
          <span>{topHammer.title}</span>
          <span className="material-symbols-outlined text-base text-n4">
            {expandedSection === 'th' ? 'expand_less' : 'expand_more'}
          </span>
        </button>
        {expandedSection === 'th' && (
          <div className="pb-2">
            <p className="px-4 text-xs text-n5 font-normal tracking-wide mb-1">By application</p>
            {renderCategoryList(topHammer.byApplication)}
            <div className="border-t border-n2 mt-2 pt-2" />
            <p className="px-4 text-xs text-n5 font-normal tracking-wide mb-1">By product</p>
            {renderCategoryList(topHammer.categories)}
          </div>
        )}
      </div>

      {/* DTH */}
      <div className="border-b border-n2">
        <button
          onClick={() => toggleSection('dth')}
          className="w-full flex items-center justify-between px-4 py-3 text-sm font-normal font-sandvik-text text-carbon-black"
        >
          <span>{dth.title}</span>
          <span className="material-symbols-outlined text-base text-n4">
            {expandedSection === 'dth' ? 'expand_less' : 'expand_more'}
          </span>
        </button>
        {expandedSection === 'dth' && (
          <div className="pb-2">
            {renderCategoryList(dth.categories)}
          </div>
        )}
      </div>

      {/* Rotary & Raiseboring — hidden while both unavailable */}
      {rotaryAndRbAvailable && (
        <div className="border-b border-n2">
          <button
            onClick={() => toggleSection('rotary')}
            className="w-full flex items-center justify-between px-4 py-3 text-sm font-normal font-sandvik-text text-carbon-black"
          >
            <span>{rotaryAndRbTitle}</span>
            <span className="material-symbols-outlined text-base text-n4">
              {expandedSection === 'rotary' ? 'expand_less' : 'expand_more'}
            </span>
          </button>
          {expandedSection === 'rotary' && (
            <div className="pb-2">
              {renderCategoryList(rotaryAndRbCategories)}
            </div>
          )}
        </div>
      )}

      {/* Services — hidden while unavailable */}
      {services?.available && (
        <div className="border-b border-n2">
          <button
            onClick={() => toggleSection('services')}
            className="w-full flex items-center justify-between px-4 py-3 text-sm font-normal font-sandvik-text text-carbon-black"
          >
            <span>{services.title}</span>
            <span className="material-symbols-outlined text-base text-n4">
              {expandedSection === 'services' ? 'expand_less' : 'expand_more'}
            </span>
          </button>
          {expandedSection === 'services' && (
            <div className="pb-2">
              {renderCategoryList(services.categories)}
            </div>
          )}
        </div>
      )}

      {/* Tool Finder promo */}
      <Link
        to="/tool-finder"
        onClick={onClose}
        className="flex items-center gap-3 px-4 py-3 hover:bg-n1 transition-colors"
      >
        <span className="material-symbols-outlined text-lg text-electric-blue">build</span>
        <div>
          <p className="text-sm font-normal font-sandvik-text text-carbon-black">Guided Tool Finder</p>
          <p className="text-xs text-n5 font-sandvik-text">The right tool for the right job</p>
        </div>
      </Link>
    </div>
  );
}

// ============================================================================
// VALUE CALCULATORS DROPDOWN — small flat panel, not a full mega menu
// ============================================================================

function ValueCalculatorsDropdown({ onClose }) {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div
      className="absolute left-0 right-0 top-full bg-white border-b border-n2 shadow-lg z-50"
      role="menu"
    >
      <div className="sandvik-container py-6">
        <div className="max-w-[520px]">
          <p className="text-xs text-n5 font-sandvik-text mb-3">
            Value calculators
          </p>
          <ul className="space-y-1">
            {VALUE_CALCULATORS.map((calc) => {
              const isAvailable = calc.available && calc.href;
              const inner = (
                <>
                  <span
                    className={`text-sm font-sandvik-text ${
                      isAvailable ? 'text-carbon-black' : 'text-n5'
                    }`}
                  >
                    {calc.label}
                  </span>
                  <span className="block text-xs text-n5 font-sandvik-text mt-0.5">
                    {calc.description}
                  </span>
                </>
              );
              return (
                <li key={calc.id}>
                  {isAvailable ? (
                    <a
                      href={calc.href}
                      onClick={onClose}
                      className="block py-2 px-3 hover:bg-n1 transition-colors"
                    >
                      {inner}
                    </a>
                  ) : (
                    <div
                      aria-disabled="true"
                      className="block py-2 px-3 cursor-not-allowed"
                    >
                      {inner}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}

function MobileValueCalculators({ onClose }) {
  return (
    <div className="absolute left-0 right-0 top-full bg-white border-b border-n2 shadow-lg z-50 max-h-[70vh] overflow-y-auto">
      <p className="px-4 pt-3 text-xs text-n5 font-sandvik-text">
        Value calculators
      </p>
      <ul>
        {VALUE_CALCULATORS.map((calc) => {
          const isAvailable = calc.available && calc.href;
          const inner = (
            <>
              <span
                className={`text-sm font-sandvik-text block ${
                  isAvailable ? 'text-carbon-black' : 'text-n5'
                }`}
              >
                {calc.label}
              </span>
              <span className="block text-xs text-n5 font-sandvik-text mt-0.5">
                {calc.description}
              </span>
            </>
          );
          return (
            <li key={calc.id} className="border-t border-n2 first:border-t-0">
              {isAvailable ? (
                <a
                  href={calc.href}
                  onClick={onClose}
                  className="block px-4 py-3 hover:bg-n1 transition-colors"
                >
                  {inner}
                </a>
              ) : (
                <div
                  aria-disabled="true"
                  className="block px-4 py-3 cursor-not-allowed"
                >
                  {inner}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function SecondaryNav() {
  const location = useLocation();
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);
  const [valueCalcOpen, setValueCalcOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const navRef = useRef(null);
  const closeTimeoutRef = useRef(null);

  const anyMenuOpen = megaMenuOpen || valueCalcOpen;

  // Responsive check
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Close any open panel on route change
  useEffect(() => {
    setMegaMenuOpen(false);
    setValueCalcOpen(false);
  }, [location.pathname]);

  // Close on click outside
  useEffect(() => {
    if (!anyMenuOpen) return;
    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setMegaMenuOpen(false);
        setValueCalcOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [anyMenuOpen]);

  const handleClose = useCallback(() => setMegaMenuOpen(false), []);
  const handleValueCalcClose = useCallback(() => setValueCalcOpen(false), []);

  const handleProductsMouseEnter = () => {
    if (isMobile) return;
    clearTimeout(closeTimeoutRef.current);
    setValueCalcOpen(false);
    setMegaMenuOpen(true);
  };

  const handleProductsMouseLeave = () => {
    if (isMobile) return;
    closeTimeoutRef.current = setTimeout(() => setMegaMenuOpen(false), 200);
  };

  const handleValueCalcMouseEnter = () => {
    if (isMobile) return;
    clearTimeout(closeTimeoutRef.current);
    setMegaMenuOpen(false);
    setValueCalcOpen(true);
  };

  const handleValueCalcMouseLeave = () => {
    if (isMobile) return;
    closeTimeoutRef.current = setTimeout(() => setValueCalcOpen(false), 200);
  };

  const handlePanelMouseEnter = () => {
    clearTimeout(closeTimeoutRef.current);
  };

  const handlePanelMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => setMegaMenuOpen(false), 200);
  };

  const handleValueCalcPanelMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => setValueCalcOpen(false), 200);
  };

  useEffect(() => () => clearTimeout(closeTimeoutRef.current), []);

  return (
    <nav ref={navRef} className="bg-white border-b border-n2 relative">
      <div className="sandvik-container">
        <div className="flex items-center gap-6 md:gap-8 h-12 overflow-x-auto scrollbar-hide">
          {NAV_ITEMS.map((item) => {
            const isActive = item.to
              ? (item.to === '/' ? location.pathname === '/' : location.pathname.startsWith(item.to))
              : false;

            const isProducts = item.hasMegaMenu;
            const isValueCalc = item.hasValueCalcMenu;
            const isTrigger = isProducts || isValueCalc;
            const isThisOpen = (isProducts && megaMenuOpen) || (isValueCalc && valueCalcOpen);

            const onEnter = isProducts
              ? handleProductsMouseEnter
              : (isValueCalc ? handleValueCalcMouseEnter : undefined);
            const onLeave = isProducts
              ? handleProductsMouseLeave
              : (isValueCalc ? handleValueCalcMouseLeave : undefined);
            const onClick = isProducts
              ? () => { setValueCalcOpen(false); setMegaMenuOpen(!megaMenuOpen); }
              : (isValueCalc ? () => { setMegaMenuOpen(false); setValueCalcOpen(!valueCalcOpen); } : undefined);

            return (
              <div key={item.label} className="flex items-center h-full">
                <div
                  className="relative h-full flex items-center"
                  onMouseEnter={onEnter}
                  onMouseLeave={onLeave}
                >
                  {isTrigger ? (
                    <button
                      onClick={onClick}
                      className={`text-sm font-sandvik-text whitespace-nowrap py-3 border-b-2 transition-colors h-full flex items-center
                        ${isActive || isThisOpen
                          ? 'text-carbon-black font-normal border-carbon-black'
                          : 'text-n6 border-transparent hover:text-carbon-black hover:border-n4'}`}
                    >
                      {item.label}
                    </button>
                  ) : (
                    <Link
                      to={item.to}
                      className={`text-sm font-sandvik-text whitespace-nowrap py-3 border-b-2 transition-colors h-full flex items-center
                        ${isActive
                          ? 'text-carbon-black font-normal border-carbon-black'
                          : 'text-n6 border-transparent hover:text-carbon-black hover:border-n4'}`}
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Mega Menu Panel */}
      {megaMenuOpen && (
        <div
          onMouseEnter={!isMobile ? handlePanelMouseEnter : undefined}
          onMouseLeave={!isMobile ? handlePanelMouseLeave : undefined}
        >
          {isMobile ? (
            <MobileMegaMenu onClose={handleClose} />
          ) : (
            <MegaMenuPanel onClose={handleClose} />
          )}
        </div>
      )}

      {/* Value Calculators Dropdown */}
      {valueCalcOpen && (
        <div
          onMouseEnter={!isMobile ? handlePanelMouseEnter : undefined}
          onMouseLeave={!isMobile ? handleValueCalcPanelMouseLeave : undefined}
        >
          {isMobile ? (
            <MobileValueCalculators onClose={handleValueCalcClose} />
          ) : (
            <ValueCalculatorsDropdown onClose={handleValueCalcClose} />
          )}
        </div>
      )}
    </nav>
  );
}
